import type { CmsPageVideos, CmsPageVideoEntry } from '../types/websiteCms.types';

/**
 * Resolves the correct video props for a given page video slot.
 *
 * Priority:
 *  1. If a `pageVideoEntries[key]` entry exists with type='upload' → return videoUrl
 *  2. If a `pageVideoEntries[key]` entry exists with type='youtube' → return videoId
 *  3. Fall back to `pageVideos[key]` (legacy YouTube ID string)
 *
 * Returns { videoId?, videoUrl? } ready to spread onto <VideoSection>.
 */
export function resolveVideoProps(
  key: keyof CmsPageVideos,
  pageVideos: CmsPageVideos,
  pageVideoEntries?: Partial<Record<keyof CmsPageVideos, CmsPageVideoEntry>>,
): { videoId?: string; videoUrl?: string; thumbnailUrl?: string } {
  const entry = pageVideoEntries?.[key];
  if (entry) {
    if (entry.type === 'upload' && entry.uploadedUrl) {
      return { videoUrl: entry.uploadedUrl, thumbnailUrl: entry.thumbnailUrl };
    }
    if (entry.type === 'youtube' && entry.youtubeId) {
      return { videoId: entry.youtubeId };
    }
  }
  // Legacy fallback — plain YouTube ID string
  const legacyId = pageVideos[key];
  return legacyId ? { videoId: legacyId } : {};
}

/**
 * Resolves video props for a CmsBeginnerVideo entry.
 * If videoType === 'upload' and uploadedVideoUrl is set, returns videoUrl.
 * Otherwise falls back to the YouTube videoId.
 */
export function resolveBeginnerVideoProps(
  video: { videoId: string; videoType?: string; uploadedVideoUrl?: string; thumbnailUrl?: string },
): { videoId?: string; videoUrl?: string; thumbnailUrl?: string } {
  if (video.videoType === 'upload' && video.uploadedVideoUrl) {
    return { videoUrl: video.uploadedVideoUrl, thumbnailUrl: video.thumbnailUrl };
  }
  return video.videoId ? { videoId: video.videoId } : {};
}
